import React, { useEffect } from 'react';
import { useGameStore } from '../stores/gameStore';
import { useDashboard } from '../hooks/useDashboard';

/**
 * DashboardAdapter - Connects dashboard components to the game engine
 * This adapter maintains the original UI but connects it to the game engine state
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Dashboard components to render
 * @param {Object} props.userStats - User statistics data
 * @param {Function} props.setUserStats - Function to update user statistics
 * @param {string} props.chartTimeframe - Current chart timeframe
 * @param {Function} props.setChartTimeframe - Function to update chart timeframe
 * @param {Array} props.recentActivity - Recent activity data
 * @returns {JSX.Element} - Original dashboard components with engine connection
 */
const DashboardAdapter = ({ 
  children, 
  userStats, 
  setUserStats,
  chartTimeframe: propChartTimeframe,
  setChartTimeframe: propSetChartTimeframe,
  getTimeframeData: propGetTimeframeData,
  recentActivity: propRecentActivity,
  ...otherProps 
}) => {
  // Connect to game store
  const dashboard = useGameStore(state => state.dashboard);
  const updateDashboard = useGameStore(state => state.updateDashboard);
  const addActivity = useGameStore(state => state.addActivity);
  const getStoreTimeframeData = useGameStore(state => state.getTimeframeData);
  
  // Use our enhanced dashboard hook
  const {
    userStats: engineUserStats,
    chartTimeframe,
    setChartTimeframe,
    getTimeframeData,
    recentActivity
  } = useDashboard(userStats);
  
  // Sync the engine state with the component props
  useEffect(() => {
    if (propSetChartTimeframe && chartTimeframe !== propChartTimeframe) {
      propSetChartTimeframe(chartTimeframe);
    }
  }, [chartTimeframe, propChartTimeframe, propSetChartTimeframe]);
  
  // Transform game engine state into dashboard props
  const enhancedUserStats = {
    ...userStats,
    // Add properties from game engine state
    solContributed: dashboard.userSolContributed,
    totalReferrals: dashboard.totalReferrals,
    totalEarnings: dashboard.totalEarnings,
    daoPower: dashboard.daoPower
  };
  
  // Clone children with enhanced props
  const enhancedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        ...otherProps,
        userStats: enhancedUserStats,
        chartTimeframe,
        setChartTimeframe,
        getTimeframeData: getTimeframeData || propGetTimeframeData,
        recentActivity: recentActivity || propRecentActivity
      });
    }
    return child;
  });
  
  return <>{enhancedChildren}</>;
};

export default DashboardAdapter;
